import {getQueryClient} from '@github-ui/query-client'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DEFAULT_SORTING_KEY} from '@github-ui/shared-dashboard-surface/constants/pulls-sorting'
import {PULLS_PATH} from '../constants/routes'
import {pullsDashboardSurfaceLayoutRoute} from '../routes/pulls-dashboard-surface-layout-route'

import type {PullsDashboardLayoutPayload} from '../types/routes-types'
import type {SidebarLink} from '@github-ui/saved-views'

// The dashboard sort dropdown defaults to "Last updated", but the backend search
// defaults to created-desc when no `sort:` qualifier is present. A search or saved
// view query without an explicit sort would therefore load in created order under a
// "Last updated" label. Append the default sort so the fetched order matches the
// control. A query that already carries a `sort:` token (including the mode queries,
// or a bare `sort:`) is left untouched, and an empty (cleared) query stays empty. The
// token check mirrors the backend SearchConfig#ensure_default_sort so the two sides
// agree and a second qualifier like `sort: sort:updated-desc` is never emitted.
export function ensureDefaultSort(query: string): string {
  if (!query) return query
  const hasSortQualifier = query.split(' ').some(term => term.startsWith('sort:'))
  if (hasSortQualifier) return query
  return `${query} sort:${DEFAULT_SORTING_KEY}-desc`
}

// !! CAUTION !! If you modify these default queries, they must also be updated on the backend: packages/dashboard_surface/app/lib/dashboard_surface/payloads/sidebar_links.rb
export function queryMap(): Record<string, string> {
  const buildQuery = (base: string) => `${base} archived:false sort:updated-desc`
  const agentExpansionEnabled = isFeatureEnabled('agent_author_search_expansion')

  return {
    authored: buildQuery(
      agentExpansionEnabled
        ? 'is:pr author:@me state:open'
        : 'is:pr (author:@me OR (author:@copilot assignee:@me)) state:open',
    ),
    assigned: buildQuery('is:pr assignee:@me state:open'),
    involves: buildQuery('is:pr involves:@me state:open'),
    reviews: buildQuery('is:pr review-requested:@me state:open'),
  }
}

function getLayoutQueryKey() {
  return pullsDashboardSurfaceLayoutRoute.queries.mainQuery.makeQueryKey({pathname: `${PULLS_PATH}/_layout`})
}

/**
 * Looks up the query for a saved view by reading the cached layout data.
 * `mode` is the URL segment, which is the saved view's GraphQL global id, or its
 * numeric id for URLs bookmarked between the tanstack cutover and github/issues#23415.
 * On cold loads the server resolves the saved view query via SearchConfig#saved_view_query,
 * so the embedded data contains correct results even if this returns undefined.
 */
function savedViewQuery(mode: string): string | undefined {
  const raw: {payload?: PullsDashboardLayoutPayload} | undefined = getQueryClient().getQueryData(getLayoutQueryKey())
  const savedViews = raw?.payload?.savedViews
  if (!savedViews) return undefined
  const view = savedViews.find(v => v.id.toString() === mode || v.globalId === mode)
  return view?.query
}

/**
 * Updates the savedViews in the TanStack Query cache so that queryResolver can resolve
 * saved view queries without ?q= in the URL.
 */
export function updateCachedSavedViews(updatedSavedViews: SidebarLink[]) {
  const queryKey = getLayoutQueryKey()
  getQueryClient().setQueryData(queryKey, (old: {payload?: PullsDashboardLayoutPayload} | undefined) => {
    if (!old?.payload) return old
    return {...old, payload: {...old.payload, savedViews: updatedSavedViews}}
  })
}

export const queryResolver = ({
  searchParams,
  params,
}: {
  searchParams: URLSearchParams
  params: {mode?: string}
}): string => {
  const map = queryMap()
  let resolved = ''
  if (searchParams.get('q')) {
    resolved = searchParams.get('q') as string
  } else if (params.mode && map[params.mode]) {
    resolved = map[params.mode] as string
  } else if (params.mode) {
    resolved = savedViewQuery(params.mode) ?? ''
  }
  return ensureDefaultSort(resolved)
}
